/* Print-A-Bill — tab-session selection restore.
 *
 * Remembers the configurator's scalar selections in sessionStorage and puts
 * them back when the customer returns to the page in the same tab (navigate
 * away and back, reload). A new tab starts fresh; explicit fresh starts
 * (#reset, mobile "new design", ?pab_reset=1 / ?new=true) clear the snapshot.
 *
 * Reuses the existing apply path (PABMockupState.restore + the custom
 * denomination / base bill / rush satellites) — the same calls
 * pab-mockup-bridge.js makes for cart-edit and post-ATC restores — so there
 * is exactly one way selections get applied. Yields to every higher-
 * precedence flow: cart edit (?pab_edit= / history build id), occasion
 * offers (?pab_offer=), and the mobile guide's own builder snapshot.
 */
(function () {
  'use strict';

  var KEY = 'pab:configurator:session:v1';
  var VERSION = 1;
  var MOBILE_BUILDER_KEYS = ['pab-mobile-guide-v1'];
  var MOBILE_QUERY = '(max-width: 767px)';
  var SAVE_DELAY_MS = 400;

  var suppressSave = false;
  var saveTimer = 0;
  var pendingSave = false;

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function storage() {
    try { return window.sessionStorage; } catch (_e) { return null; }
  }

  /* ------------------------------------------------------------------ */
  /* Collect the live selections in the exact shape PABMockupState.      */
  /* restore() consumes (booleans are strict — it compares with ===!0).  */
  /* ------------------------------------------------------------------ */
  function collectSpec() {
    var root = document.getElementById('pab-mockup-root');
    if (!root) return null;
    var fmt = window.PABMockupState && typeof window.PABMockupState.getState === 'function'
      ? window.PABMockupState.getState()
      : null;
    if (!fmt) return null;

    var paperInput = root.querySelector('#paper input:checked');
    var paperLabel = paperInput ? String(paperInput.dataset.label || '') : '';
    var paper = paperLabel === 'Flame-Retardant' ? 'flame'
      : paperLabel === 'Premium Durable' ? 'durable' : 'signature';

    var realaged = $('#realaged-option', root);
    var uv = $('#uv-option', root);
    var serials = $('#serial-option', root);
    var denomChangeOpt = $('#denom-change-option', root);
    var tier = String(fmt.tier || 'premium').toLowerCase();
    var denomChange = tier === 'ultimate' || !!(denomChangeOpt && denomChangeOpt.checked);

    var rushLegacy = document.getElementById('rushService');
    var rush = !!(rushLegacy && rushLegacy.checked) ||
      !!(window.PABRushService && typeof window.PABRushService.get === 'function' && window.PABRushService.get());

    var customDenomination = '';
    try {
      customDenomination = window.PABCustomDenomination && typeof window.PABCustomDenomination.value === 'function'
        ? String(window.PABCustomDenomination.value() || '') : '';
    } catch (_e) { /* keep default */ }

    var accessoryItems = [];
    $$('.accessory[data-variant]', root).forEach(function (card) {
      var strong = card.querySelector('strong');
      var qty = strong ? parseInt(String(strong.textContent || '').replace(/[^0-9]/g, ''), 10) : 0;
      if (isFinite(qty) && qty > 0) {
        accessoryItems.push({ variantId: String(card.dataset.variant || ''), qty: qty });
      }
    });

    var nameInput = root.querySelector('#design-name');

    return {
      tier: tier,
      stacks: fmt.stacks,
      billName: nameInput ? String(nameInput.value || '').trim() : '',
      billSeries: fmt.billSeries,
      denomination: fmt.denomination,
      customDenomination: customDenomination,
      baseBill: fmt.baseBill,
      paper: paper,
      finish: realaged && realaged.checked ? 'realaged' : 'standard',
      uv: !!(uv && uv.checked),
      serials: !!(serials && serials.checked),
      rush: rush,
      denomChange: denomChange,
      accessoryItems: accessoryItems
    };
  }

  function writeSnapshot(spec) {
    var store = storage();
    if (!store || suppressSave) return false;
    try {
      store.setItem(KEY, JSON.stringify({ v: VERSION, savedAt: Date.now(), spec: spec }));
      return true;
    } catch (_e) { return false; }
  }

  function readSnapshot() {
    var store = storage();
    if (!store) return null;
    var raw;
    try { raw = store.getItem(KEY); } catch (_e) { return null; }
    if (!raw) return null;
    try {
      var record = JSON.parse(raw);
      if (!record || record.v !== VERSION || !record.spec || typeof record.spec !== 'object') return null;
      return record.spec;
    } catch (_e) { return null; }
  }

  function clearSnapshot() {
    var store = storage();
    if (!store) return;
    try { store.removeItem(KEY); } catch (_e) { /* ignore */ }
  }

  function suppressAndClear() {
    suppressSave = true;
    pendingSave = false;
    if (saveTimer) { try { clearTimeout(saveTimer); } catch (_e) { /* ignore */ } saveTimer = 0; }
    clearSnapshot();
  }

  function saveNow() {
    pendingSave = false;
    if (suppressSave) return;
    var spec = null;
    try { spec = collectSpec(); } catch (_e) { /* ignore */ }
    if (spec) writeSnapshot(spec);
  }

  function markDirty() {
    if (suppressSave) return;
    pendingSave = true;
    if (saveTimer) { try { clearTimeout(saveTimer); } catch (_e) { /* ignore */ } }
    saveTimer = window.setTimeout(function () { saveTimer = 0; saveNow(); }, SAVE_DELAY_MS);
  }

  /* ------------------------------------------------------------------ */
  /* Apply a snapshot through the same calls the bridge uses.            */
  /* ------------------------------------------------------------------ */
  function applySnapshot(spec) {
    if (!spec || typeof spec !== 'object') return false;
    try {
      var rush = document.getElementById('rushService');
      if (rush) rush.checked = !!spec.rush;
      if (window.PABRushService && typeof window.PABRushService.set === 'function') {
        window.PABRushService.set(!!spec.rush);
      }
      if (window.PABMockupState && typeof window.PABMockupState.restore === 'function') {
        window.PABMockupState.restore({
          tier: spec.tier, stacks: spec.stacks, billName: spec.billName,
          billSeries: spec.billSeries, denomination: spec.denomination, baseBill: spec.baseBill,
          paper: spec.paper, finish: spec.finish, uv: spec.uv, serials: spec.serials,
          denomChange: spec.denomChange, accessoryItems: spec.accessoryItems
        });
      } else {
        return false;
      }
      if (window.PABCustomDenomination && typeof window.PABCustomDenomination.restore === 'function') {
        window.PABCustomDenomination.restore(spec.customDenomination || '');
      }
      if (window.PABBaseBill && typeof window.PABBaseBill.restore === 'function') {
        window.PABBaseBill.restore(spec.baseBill || '');
      }
      return true;
    } catch (_e) { return false; }
  }

  /* ------------------------------------------------------------------ */
  /* Precedence: only restore when no other flow owns the page.          */
  /* ------------------------------------------------------------------ */
  function queryParam(name) {
    try { return new URLSearchParams(window.location.search).get(name); } catch (_e) { return null; }
  }

  function hasRequestedBuildId() {
    try {
      var explicit = queryParam('pab_edit');
      if (explicit && String(explicit).trim()) return true;
      if (window.PAB && typeof window.PAB.currentConfiguratorHistoryBuildId === 'function' &&
        window.PAB.currentConfiguratorHistoryBuildId()) return true;
    } catch (_e) { /* ignore */ }
    return false;
  }

  function hasMobileBuilderSnapshot() {
    var store = storage();
    var candidates = [];
    try {
      if (store) candidates.push(store.getItem(MOBILE_BUILDER_KEYS[0]));
      candidates.push(window.localStorage.getItem(MOBILE_BUILDER_KEYS[0]));
    } catch (_e) { /* ignore */ }
    return candidates.some(function (raw) {
      if (!raw) return false;
      try {
        var record = JSON.parse(raw);
        return !!(record && record.builder);
      } catch (_err) { return false; }
    });
  }

  function isMobileWidth() {
    try { return window.matchMedia(MOBILE_QUERY).matches; } catch (_e) { return false; }
  }

  function shouldRestore() {
    if (!document.getElementById('pab-mockup-root')) return false;
    if (queryParam('pab_reset') === '1' || queryParam('new') === 'true') return false;
    if (queryParam('pab_offer')) return false;
    if (hasRequestedBuildId()) return false;
    // The mobile guide owns resume on phones when it has its own snapshot.
    if (isMobileWidth() && hasMobileBuilderSnapshot()) return false;
    return true;
  }

  /* Latched at first run: the page's identity (fresh builder, cart edit,
   * offer) cannot change during load, but the URL can — the bridge strips
   * ?pab_reset=1 after honoring it, and a later re-check would wrongly
   * pass and undo its reset. */
  var gatePass = null;

  function tryRestore() {
    if (gatePass === null) gatePass = shouldRestore();
    if (!gatePass) return false;
    var spec = readSnapshot();
    if (!spec) return false;
    return applySnapshot(spec);
  }

  function installResetHooks() {
    // Desktop "start over" reloads the page — clear first so the unload
    // flush below cannot re-save the about-to-be-discarded selections.
    document.addEventListener('click', function (event) {
      var target = event.target && event.target.closest ? event.target.closest('#reset') : null;
      if (target) suppressAndClear();
    }, true);
    // Mobile welcome "new design" uses this class (see pab-mobile-welcome-boot.js).
    document.addEventListener('click', function (event) {
      var target = event.target && event.target.closest ? event.target.closest('.pab-mobile-intro__new') : null;
      if (target) suppressAndClear();
    }, true);
    // Every other fresh start (mobile guide "start over", future flows)
    // funnels through this flag — catch them all in one place.
    try {
      var originalSetItem = window.sessionStorage.setItem.bind(window.sessionStorage);
      window.sessionStorage.setItem = function (key, value) {
        if (key === 'pab-mobile-new-design-v1' && String(value) === 'true') suppressAndClear();
        return originalSetItem(key, value);
      };
    } catch (_e) { /* ignore */ }
  }

  function installSaveHooks() {
    var root = document.getElementById('pab-mockup-root');
    if (root) {
      ['change', 'input'].forEach(function (type) {
        root.addEventListener(type, markDirty, { passive: true });
      });
      // Chips and steppers act on click; change/input already cover most,
      // this catches anything they miss.
      root.addEventListener('click', markDirty, { passive: true });
    }
    window.addEventListener('pagehide', function () {
      if (saveTimer) { try { clearTimeout(saveTimer); } catch (_e) { /* ignore */ } saveTimer = 0; }
      saveNow();
    });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') saveNow();
    });
  }

  /* Runs synchronously at deferred-script execution (DOM parsed, earlier
   * APIs defined) so restored selections beat first paint, then converges
   * on DOMContentLoaded and again once live prices/availability hydrate
   * (accessory availability can flip cards from unavailable to available,
   * which changes what restore is allowed to set). */
  function converge() {
    tryRestore();
    try {
      var jobs = [];
      if (window.PAB && typeof window.PAB.ensureVariantPrices === 'function') {
        jobs.push(window.PAB.ensureVariantPrices());
      }
      if (window.PAB && typeof window.PAB.ensureAccessoryPrices === 'function') {
        jobs.push(window.PAB.ensureAccessoryPrices());
      }
      if (jobs.length) {
        Promise.all(jobs.map(function (p) {
          try { return Promise.resolve(p).catch(function () { /* ignore */ }); }
          catch (_e) { return Promise.resolve(); }
        })).then(function () { tryRestore(); });
      }
    } catch (_e) { /* ignore */ }
  }

  installResetHooks();
  installSaveHooks();
  tryRestore();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', converge, { once: true });
  } else {
    converge();
  }

  window.PABSessionRestore = {
    saveNow: function () { saveNow(); return true; },
    clear: function () { suppressAndClear(); return true; },
    collectSpec: collectSpec
  };
})();
